//codex-- Reuse the calendar's inspection form and exact API contracts for creation from a map pin.
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import InspectionFormModal from './InspectionFormModal';
import { contractMapAddress } from '../utils/contractMap';

//codex-- Compute today's local date when opening, independently of the month/year displayed on the map.
export function createMapoutInspectionDraft(contract, now = new Date()) {
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  return {
    maintenance_id: contract.maintenance_id, job_site_name: contractMapAddress(contract), next_due_date: contract.next_due_date || '',
    scheduled_due_date: today, schedule_date_to: today, actual_inspection_date: '',
    lead_tech_id: '', additional_tech_id: '', schedule_time_in: '', schedule_time_out: '',
    status: 'pending', tentative: false, status_description: '', inspection_notes: '', invoiced_file: '',
  };
}

//codex-- Fetch technicians only while this new-inspection flow is mounted; retain drafts on failed saves.
export default function MapoutInspectionModal({ contract, onClose, onSaved }) {
  const [formData, setFormData] = useState(() => createMapoutInspectionDraft(contract));
  const [technicians, setTechnicians] = useState([]);
  const [techState, setTechState] = useState('loading');
  const [retry, setRetry] = useState(0);
  const [saving, setSaving] = useState(false);
  const submitting = useRef(false);
  const apiUrl = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();

  //codex-- Match InspectionCalendarView's employee-list request and department/last-name ordering.
  useEffect(() => {
    const controller = new AbortController();
    setTechState('loading');
    axios.get(`${apiUrl}/contracts_crud.php`, {
      params: { employee_list: 'true' }, signal: controller.signal,
      headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
    }).then(response => {
      if (controller.signal.aborted) return;
      if (!Array.isArray(response.data) || !response.data.length || response.data.some(tech => !tech || tech.id == null)) throw new Error('No technicians available');
      setTechnicians([...response.data].sort((a, b) =>
        (a.department || '').localeCompare(b.department || '', undefined, { sensitivity: 'base' })
        || (a.last_name || '').localeCompare(b.last_name || '', undefined, { sensitivity: 'base' })));
      setTechState('ready');
    }).catch(error => {
      if (controller.signal.aborted) return;
      setTechState('error');
      if (error.response?.status === 401) navigate('/login');
    });
    return () => controller.abort();
  }, [apiUrl, navigate, retry]);

  //codex-- Validate required selections and calendar rules before the existing recordInspection POST; guard rapid duplicates.
  const submit = async event => {
    event.preventDefault();
    if (submitting.current) return;
    if (techState !== 'ready') { toast.error('Load technicians before creating the inspection.'); return; }
    if (!formData.maintenance_id || !formData.job_site_name || !formData.scheduled_due_date || !formData.schedule_date_to
      || !formData.schedule_time_in || !formData.schedule_time_out || !formData.lead_tech_id) {
      toast.error('Select a jobsite and complete the required schedule and technician fields.'); return;
    }
    if (formData.scheduled_due_date > formData.schedule_date_to) { toast.error('Scheduled "from" date cannot be later than "to" date.'); return; }
    if (formData.additional_tech_id && String(formData.lead_tech_id) === String(formData.additional_tech_id)) {
      toast.error('You cannot use the same technician as lead and additional.'); return;
    }
    if (formData.tentative && !formData.status_description.trim()) { toast.error('Please provide a Status Description for tentative inspections.'); return; }
    submitting.current = true;
    setSaving(true);
    try {
      const response = await axios.post(`${apiUrl}/contracts_crud.php`, { data: formData, status: 'recordInspection' });
      if (!response.data?.success) { toast.error('Failed to save inspection. Please try again.'); return; }
      toast.success('Inspection saved successfully!');
      onSaved();
    } catch (error) {
      if (error.response?.status === 401) { toast.error('Session expired. Please login again.'); navigate('/login'); }
      else if (error.response?.status === 403) toast.error('Access denied.');
      else toast.error('Failed to save inspection. Please try again.');
    } finally {
      submitting.current = false;
      setSaving(false);
    }
  };

  //codex-- Keep retry inside the modal and block dismissal/edits during the pending save.
  return <InspectionFormModal isOpen onClose={() => { if (!submitting.current) onClose(); }}
    formData={formData} setFormData={setFormData} onSubmit={submit} technicians={technicians}
    isEditing={false} disabled={false} loading={saving || techState === 'loading'} blockWhileLoading
    loadingLabel={saving ? 'Saving inspection…' : 'Loading technicians…'}
    formNotice={techState === 'error' ? <div role="alert" className="rounded-lg bg-orange-50 p-3 text-sm text-orange-900">
      Technicians could not be loaded. <button type="button" className="font-semibold underline" onClick={() => setRetry(value => value + 1)}>Retry technicians</button>
    </div> : null} />;
}
